import { LLMProvider } from '../enums';

/**
 * Safe env access that works in both backend (Node/Bun) and browser (portals).
 * Returns undefined in browser where `process` doesn't exist.
 * Treats empty strings as unset to prevent misconfigured env vars from
 * bypassing fallback defaults (e.g., OPENAI_DEFAULT_MODEL="").
 */
function getEnv(key: string): string | undefined {
  try {
    const value = typeof process !== 'undefined' ? process.env[key] : undefined;
    return value?.trim() || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Default LLM models by provider.
 *
 * These are the recommended models for general-purpose completion.
 * Override per-provider via env vars: OPENAI_DEFAULT_MODEL, GEMINI_DEFAULT_MODEL, ANTHROPIC_DEFAULT_MODEL.
 *
 * NOTE: gpt-5-mini is a reasoning model (o-series behavior) that does NOT support
 * temperature/top_p. Use gpt-4o-mini for standard completions.
 *
 * Gemini default is `gemini-3.1-flash-lite-preview` — `gemini-2.5-flash` is deprecated
 * (shutdown scheduled June 17, 2026) and the public Gemini API no longer serves
 * it on the current tier; `gemini-3.1-flash-lite-preview` is Google's official
 * replacement. See CLAUDE.md §16.
 */
export const DEFAULT_LLM_MODELS: Record<string, string> = {
  [LLMProvider.OPENAI]: getEnv('OPENAI_DEFAULT_MODEL') ?? 'gpt-4o-mini',
  [LLMProvider.GEMINI]: getEnv('GEMINI_DEFAULT_MODEL') ?? 'gemini-3.1-flash-lite-preview',
  [LLMProvider.ANTHROPIC]: getEnv('ANTHROPIC_DEFAULT_MODEL') ?? 'claude-haiku-4.5',
};

/**
 * Get the default model for a given provider.
 * Falls back to the OpenAI default if provider is unknown.
 */
export function getDefaultModel(provider: string): string {
  return DEFAULT_LLM_MODELS[provider.toLowerCase()] ?? DEFAULT_LLM_MODELS[LLMProvider.OPENAI]!;
}

/**
 * The platform-wide default model (primary provider: OpenAI).
 * Override via OPENAI_DEFAULT_MODEL env var.
 */
export const DEFAULT_LLM_MODEL = DEFAULT_LLM_MODELS[LLMProvider.OPENAI]!;
